import React, {useEffect, useState} from 'react';
import {Button, Form, Input, Select, Modal, Divider, Row, Col} from 'antd';
import {CommandDto, CommandDtoValueOptionEnum, LocateDto} from '../../../api';
import {EditOutlined, FunctionOutlined, SaveOutlined} from "@ant-design/icons";
import {getLabelWithNameAndInfotext} from "../../../utils/Utils";
import DescriptionFormItem from "../../form/DescriptionFormItem";
import ExpressionBuilderModal from "../ExpressionBuilderModal";
import LocatorList from "./locator/LocatorList";
import {listToLocateDto, locateDtoToList} from "../../../utils/LocatorUtils";

const SaveValueCommandModal = ({onCommandCardData, initialData}: { onCommandCardData: Function; initialData?: CommandDto }) => {
    const {Option} = Select;
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false); // State für die Modal-Sichtbarkeit
    const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
    const [isExpressionBuilderVisible, setIsExpressionBuilderVisible] = useState(false);

    // Die Locators
    const [locatorData, setLocatorData] = useState<LocateDto[]>();

    useEffect(() => {
        // Falls der ExecuteValue eine spezielle Funktion ist und dort etwas aufgelöst werden soll
        if (initialData?.executeValue) form.setFieldValue('executeValue', initialData.executeValue);
        if (initialData?.jsonPath) form.setFieldValue('jsonPath', initialData.jsonPath);
        if (initialData?.variableName) form.setFieldValue('variableName', initialData.variableName);
        if (initialData?.attribute) form.setFieldValue('attribute', initialData.attribute);
        if (initialData?.description) form.setFieldValue('description', initialData.description);
        if (initialData?.valueOption) {
            form.setFieldValue('valueOption', initialData.valueOption);
            handleOptionChange(initialData.valueOption);
        }
        if (initialData?.locateDto) setLocatorData(locateDtoToList(initialData.locateDto));

    }, [initialData]);


    const onFinish = (values) => {
        let locate: LocateDto = listToLocateDto(locatorData);

        let command: CommandDto = {
            locateDto: locate,
            commandType: 'SAVE_VALUE',
            valueOption: values.valueOption,
            variableName: values.variableName,
            jsonPath: values.jsonPath,
            attribute: values.attribute,
            description: values.description,
            executeValue: values.executeValue
        };
        onCommandCardData(command);
        setIsModalVisible(false); // Modal schließen
    };

    const onReset = () => {
        form.resetFields();
    };

    const allStatusOptions = Object.values(CommandDtoValueOptionEnum);

    // Funktion zum Öffnen des Modals
    const showModal = () => {
        setIsModalVisible(true);
    };

    // Funktion zum Schließen des Modals
    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleOptionChange = (value: string) => {
        setSelectedOption(value);
    };


    const handleOpenExpressionBuilder = () => {
        setIsExpressionBuilderVisible(true);
    };

    const handleExpressionBuilderCancel = () => {
        setIsExpressionBuilderVisible(false);
    };

    return (
        <>
            {initialData == null &&
                <div style={{display: 'flex', alignItems: 'stretch', justifyContent: 'stretch'}}>
                  <Button
                      type="primary"
                      onClick={showModal}
                      style={{width: '100%', height: '100%'}}
                  >
                    <SaveOutlined/> Variable speichern
                  </Button>
                </div>}
            {initialData != null &&
                <Button
                    type="primary"
                    style={{float: 'right'}}
                    onClick={showModal}
                >
                  <EditOutlined/>
                </Button>
            }
            <Modal
                title="Speichern von Variable"
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    name="control-hooks"
                    onFinish={onFinish}
                    layout="vertical"
                >
                    <LocatorList locateDtoList={locatorData} onChange={setLocatorData} />
                    <Divider/>
                    <Form.Item name="valueOption" label={getLabelWithNameAndInfotext("Selektionsoption",
                        "INPUT_VALUE: Wert aus einem Eingabefeld",
                        "INNER_TEXT: Sichtbarer Textinhalt, wie im Browser",
                        "TEXT_CONTENT: Gesamter Textinhalt (auch versteckte)",
                        "INNER_HTML: Alle HTML-Elemente im ausgewählten Element",
                        "ATTRIBUTE: Inhalt eines HTML-Attributes")}
                               rules={[{required: true, message: 'Bitte wähle eine Selektionsoption aus'}]}>
                        <Select placeholder="Selektionsoption auswählen" allowClear onChange={handleOptionChange}>
                            {allStatusOptions.map((option) => (
                                <Option key={option} value={option}>
                                    {option}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {selectedOption === 'ATTRIBUTE' &&
                        <Form.Item name="attribute" label={getLabelWithNameAndInfotext("Attribute", "Hier kann ein HTML-Attribute" +
                            "wie zum Beispiel 'href' angegeben werden, sodass der Inhalt des Attributs ausgewählt wird ")}
                                   rules={[{required: true, message: 'Bitte gebe ein Attribut ein'}]}>
                          <Input/>
                        </Form.Item>
                    }
                    <Form.Item name="executeValue" label={getLabelWithNameAndInfotext("Funktion", "Es kann eine " +
                        "Funktion auf den lokalisierten Wert ausgeführt werden, bevor dieser in der Variable gespeichert wird")}>
                        <Input addonAfter={<Button onClick={handleOpenExpressionBuilder}><FunctionOutlined/></Button>}/>
                    </Form.Item>
                    <Form.Item name="jsonPath" label={getLabelWithNameAndInfotext("Jsonpfad", "Sollte der " +
                        "lokalisierte Wert ein JSON sein, so kann hier ein Pfad angegeben werden, um auf ein bestimmtes Attribut " +
                        "zugreifen zu können", "z.B. konto.vertrag[0].vorname")}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="variableName" label="Variablenname" rules={[{required: true, message: 'Bitte gebe einen Variablennamen ein'}]}>
                        <Input/>
                    </Form.Item>
                    <DescriptionFormItem/>
                    <Form.Item>
                        <Row justify="center">
                            <Col style={{marginRight: 5}}>
                                <Button type="primary" htmlType="submit">
                                    {initialData ? "Speichern" : "Hinzufügen"}
                                </Button>
                            </Col>
                            <Col>
                                <Button htmlType="button" onClick={onReset}>
                                    Zurücksetzen
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
                <ExpressionBuilderModal
                    isVisible={isExpressionBuilderVisible}
                    onSave={(expression) => {form.setFieldValue('executeValue', expression)}}
                    thisOption={true}
                    handleCancel={handleExpressionBuilderCancel}
                />
            </Modal>
        </>
    );
};

export default SaveValueCommandModal;
